/**
 * The two desks of the Read board: the page as it was printed, and what was read off it.
 *
 * They turn together. A reading is checked against the page it came off, so the bars on the
 * right are the bars of the page on the left wherever the reading says which those are, and
 * the one pager, over the page, turns both. A page with no music on it, a cover or a page of
 * words, was shown to no reader, and the right says so. Side by side from `xl`; narrower than
 * that one desk at a time under a switch, as the Lab's two are, and both stay mounted, so the
 * reading is heard on where it was left.
 *
 * The desk draws one page's bars and what leaves it is the whole reading: every download, the
 * editor, and Arrange, which is handed a reading only where its reader's may be shipped. While
 * a reader reads page by page, what it has read so far is on the desk, a page still being
 * read said to be; it goes to Arrange only once it is whole.
 */

import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import ReadSheet, { Pager } from "./ReadSheet";
import ScoreAudition from "./ScoreAudition";
import { count } from "../lib/format";
import { onPage } from "../lib/read-count";
import { SOFAR, type Counted, type Shown } from "../lib/read-runs";
import { original } from "../lib/score-window";

const DESKS = ["page", "read"] as const;

type Desk = (typeof DESKS)[number];

function Empty({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-[220px] flex-1 items-center justify-center rounded-md border border-dashed border-shell-seam bg-canvas/40 p-8 text-center xl:min-h-0">
      <p className="max-w-sm text-[12px] leading-relaxed text-shell-dim">{children}</p>
    </div>
  );
}

export interface ReadDesksProps {
  /** The scan's name, which the files saved from the reading are named after. */
  readonly name: string;
  readonly pdf: PDFDocumentProxy | null;
  /** Why this browser could not open the scan to draw it. */
  readonly unopened: string | null;
  readonly page: number;
  onPage(page: number): void;
  /** The reading on the desk, and its count where it has one. */
  readonly run: Shown | null;
  readonly counted: Counted | undefined;
  /** Whether a reader is reading the scan now. */
  readonly reading: boolean;
  onOpenInEditor(musicxml: string): void;
  /** Hand a reading to Arrange; asked only of a reader whose readings may be shipped. */
  onArrange(run: Shown): void;
}

export default function ReadDesks({
  name,
  pdf,
  unopened,
  page,
  onPage: turn,
  run,
  counted,
  reading,
  onOpenInEditor,
  onArrange,
}: ReadDesksProps): JSX.Element {
  const [front, setFront] = useState<Desk>("page");
  const shown = (desk: Desk): string =>
    `${desk === front ? "flex" : "hidden"} min-h-0 flex-1 flex-col xl:flex`;

  // The bars of this page, where the reading says which they are; `null` where it does not
  // say, and they are laid out from the first; `undefined` where the page has none.
  const span =
    run === null || run.reading.spans === null
      ? null
      : run.reading.spans.find((one) => one.page === page);
  const whole = run?.score.score ?? null;
  const first = span?.first ?? 1;
  const last = span?.last ?? whole?.measures.length ?? 1;
  const bars = useMemo(
    () => (run === null ? null : original(name, run.score, first, last)),
    [name, run, first, last],
  );
  const here =
    span == null || counted === undefined || typeof counted === "string"
      ? null
      : onPage(counted, span);
  const sofar = run?.id === SOFAR;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 xl:flex-row xl:overflow-hidden">
      <div role="tablist" aria-label="Desk" className="flex shrink-0 gap-1 xl:hidden">
        {DESKS.map((desk) => (
          <button
            key={desk}
            type="button"
            role="tab"
            aria-selected={desk === front}
            onClick={() => setFront(desk)}
            className={`h-7 rounded px-3 text-[11px] ${
              desk === front
                ? "bg-shell-key text-shell-lit"
                : "text-shell-dim hover:bg-shell-hot hover:text-shell-lit"
            }`}
          >
            {desk === "page" ? "The page" : "As read"}
          </button>
        ))}
      </div>

      <div className={shown("page")}>
        {pdf !== null ? (
          <ReadSheet pdf={pdf} page={page} onPage={turn} />
        ) : (
          <>
            {/* Undrawn, the scan still has its pages, and the reading is turned through them. */}
            {run !== null && (
              <div className="flex shrink-0 justify-end pb-1">
                <Pager page={page} pages={run.reading.pages} onPage={turn} />
              </div>
            )}
            <Empty>
              {unopened === null
                ? "Opening the scan…"
                : `This browser could not draw the scan: ${unopened}. A reader opens it with its own program, so a reading may still work.`}
            </Empty>
          </>
        )}
      </div>

      <div className={shown("read")}>
        {run === null || bars === null ? (
          <Empty>
            {reading
              ? "Being read. Each page is laid out here as soon as it has been read."
              : "Not read yet. Pick who reads it and press Read."}
          </Empty>
        ) : run.reading.skipped.includes(page) ? (
          <Empty>
            There is no music on this page, so it was not read. Turn to a page of music for what{" "}
            {run.reader.label} read.
          </Empty>
        ) : run.reading.waiting.includes(page) ? (
          <Empty>
            {run.reader.label} is still reading this page. It is laid out here as soon as it has
            been read; turn to another page for what has been read so far.
          </Empty>
        ) : run.reading.unread.includes(page) ? (
          <Empty>
            {run.reader.label} failed on this page, so it is left out of the reading. Turn to
            another page for what it read.
          </Empty>
        ) : span === undefined ? (
          <Empty>{run.reader.label} read nothing off this page.</Empty>
        ) : "reason" in bars ? (
          <Empty>
            {run.reader.label} wrote a score this browser could not draw: {bars.reason}
          </Empty>
        ) : (
          <ScoreAudition
            score={bars.score}
            sound={whole ?? bars.score}
            musicxml={run.reading.musicxml}
            fileName={`${name.replace(/\.pdf$/i, "")}-${run.reader.name}.musicxml`}
            heading={`As read by ${run.reader.label}, bars ${bars.bars}${
              here === null
                ? ""
                : ` · ${count(here.right)} of ${count(here.notes)} notes the score's`
            }`}
            note={
              span === null
                ? `${run.reading.note} It does not say which page its bars came off, so they are laid out from the first whichever page is turned to.`
                : sofar
                  ? `${run.reading.note} These are this page's bars; every download and the editor get what has been read so far, and Arrange the whole once it is read.`
                  : `${run.reading.note} These are this page's bars; ${
                      run.reader.shipped
                        ? "every download, the editor and Arrange get"
                        : "every download and the editor get"
                    } the whole reading.`
            }
            onOpenInEditor={() => onOpenInEditor(run.reading.musicxml)}
            onArrange={run.reader.shipped && !sofar ? () => onArrange(run) : undefined}
          />
        )}
      </div>
    </div>
  );
}
